import { type JSX, type RefObject, useEffect, useState } from "react";

import { loadReferences } from "@frontend/lib/references";
import type { Reference } from "@frontend/lib/references";
import { ContactField } from "@frontend/shared/contact-field";
import { InterlocutorDialog } from "@frontend/shared/interlocutor-dialog";
import { useLingui } from "@lingui/react/macro";

import { clientApi } from "../services";
import type { ClientContact } from "../types";

export type ClientContactDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: number | null;
  onCreated: (contact: ClientContact) => void;
  originRef?: RefObject<DOMRect | null>;
};

const empty = { personId: null, typeId: null };

export function ClientContactDialog({
  open,
  onOpenChange,
  clientId,
  onCreated,
  originRef,
}: ClientContactDialogProps): JSX.Element {
  const { t } = useLingui();

  const [parts, setParts] = useState<Reference[]>([]);
  const [held, setHeld] = useState<number[]>([]);

  useEffect(() => {
    if (!open) {
      return;
    }

    void loadReferences("clientContactTypes").then(setParts);
  }, [open]);

  useEffect(() => {
    if (!open || null === clientId) {
      return;
    }

    void clientApi
      .fetchClient(clientId)
      .then((client) =>
        setHeld(
          client.contacts
            .map((contact) => contact.contactId)
            .filter((id): id is number => null !== id),
        ),
      );
  }, [open, clientId]);

  return (
    <InterlocutorDialog
      open={open}
      onOpenChange={onOpenChange}
      originRef={originRef}
      icon="UserPlus"
      title={t`New contact`}
      personLabel={t`Contact`}
      personControl={(field) => <ContactField field={field} exclude={held} />}
      parts={parts}
      values={empty}
      onSubmit={async ({ personId, typeId }) => {
        if (null === clientId || null === personId) {
          return;
        }

        onCreated(
          await clientApi.createClientContact(clientId, {
            contactId: personId,
            typeId,
          }),
        );
      }}
    />
  );
}
